import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertCircle, Check, ExternalLink, Film, ImagePlus, Loader2, Plus, RefreshCw, Send, Sparkles, Trash2, Users, X,
} from "lucide-react";
import {
  listSocialPosts, listSocialAccounts, createSocialPost, updateSocialPost, deleteSocialPost, attachSocialImage,
  removeSocialImage, attachSocialVideo, removeSocialVideo, publishSocialPost, mediaUrl, generateSocialImage,
  generateMissingSocialImages, getMe,
} from "../lib/api";
import AccountsPanel from "./AccountsPanel";
import SocialAiModal from "./SocialAiModal";
import ModelPicker from "./ModelPicker";
import { CharMeter, PostPreview, RewriteBar } from "./PostParts";
import ImagePanel, { imageNode } from "./ImagePanel";
import { emptyImage, savePostWithImage } from "../lib/savePost";
import { PLATFORMS } from "../lib/platforms";
import { createPortal } from "react-dom";

const POST_STATUS = {
  draft: { label: "Draft", tone: "neutral" },
  partial: { label: "Partly posted", tone: "yellow" },
  posted: { label: "Posted", tone: "green" },
  failed: { label: "Failed", tone: "hot" },
};
const RESULT_TONE = { posted: "green", failed: "hot", skipped: "yellow" };
const MAX_VIDEO_MB = 250;

function Tag({ tone = "neutral", children }) { return <span className={`badge badge-${tone}`}>{children}</span>; }
const when = (iso) => iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
const errText = (e, fallback) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : fallback; };

function readAsBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] || "");
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function ConfirmPublish({ post, names, close, onConfirm, busy }) {
  return <div className="modal-backdrop" onClick={close}><div className="modal" onClick={e => e.stopPropagation()} data-testid="social-confirm-modal">
    <button className="modal-close" onClick={close}><X size={18} /></button>
    <div className="modal-icon"><Send size={19} /></div>
    <h2>Post to {names.length} account{names.length === 1 ? "" : "s"}?</h2>
    <p>"{post.title}" goes live right away on: <b>{names.join(", ")}</b>. Editing it here afterwards won't change what's already posted.</p>
    <button className="primary-button full" disabled={busy} onClick={onConfirm} data-testid="social-confirm-post">{busy ? <Loader2 size={14} className="spin" /> : <Send size={14} />} Post now</button>
  </div></div>;
}

function NewPostModal({ close, onCreated, onWriteWithAi }) {
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [headline, setHeadline] = useState("");
  const [sub, setSub] = useState("");
  const [image, setImage] = useState(emptyImage());
  const [platform, setPlatform] = useState("linkedin");
  const [model, setModel] = useState({ provider: "auto", model: "" });
  const [aiLabel, setAiLabel] = useState(false);
  const [showModel, setShowModel] = useState(false);
  const [busy, setBusy] = useState(false);
  const tag = PLATFORMS[platform]?.label;
  const save = async () => {
    setBusy(true);
    try {
      const { post, notes } = await savePostWithImage({ text, title, category: "Custom", aiLabel, image, headline, sub, tag });
      notes.forEach(n => toast.message(n));
      onCreated(post); close();
    } catch (e) { toast.error(errText(e, "Could not create the post")); }
    finally { setBusy(false); }
  };
  return createPortal(<div className="modal-backdrop studio-backdrop"><div className="composer" data-testid="social-new-modal">
    <header className="studio-head">
      <div><h2><Plus size={18} /> New post</h2><p>Write once, then pick which of your connected accounts it goes to.</p></div>
      <div className="composer-head-actions">
        <button className="outline-button" onClick={onWriteWithAi} data-testid="composer-ai"><Sparkles size={14} /> Write it for me</button>
        <button className="icon-button" onClick={close} title="Close"><X size={16} /></button>
      </div>
    </header>
    <div className="composer-body">
      <section className="composer-edit">
        <label><span>Title <small>(only you see this)</small></span><input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Spring sale" data-testid="social-new-title" /></label>
        <label><span>Post text</span><textarea value={text} onChange={e => setText(e.target.value)} rows={8} placeholder="Write your post, or paste it here…" data-testid="social-new-text" /></label>
        <CharMeter text={text} platform={platform} />
        <RewriteBar text={text} platform={platform} model={model} onResult={(t) => { setText(t); setAiLabel(true); }} />
        <button type="button" className="text-button" onClick={() => setShowModel(v => !v)}>{showModel ? "Hide" : "Choose"} the AI model</button>
        {showModel && <ModelPicker value={model} onChange={setModel} compact />}
        <ImagePanel image={image} onChange={setImage} text={text} headline={headline} sub={sub} tag={tag} onHeadline={setHeadline} onSub={setSub} textModel={model} />
        <label className="check-inline"><input type="checkbox" checked={aiLabel} onChange={e => setAiLabel(e.target.checked)} /> Mark as AI-assisted (some platforms ask for this)</label>
      </section>
      <section className="composer-preview">
        <div className="preview-tabs">{Object.entries(PLATFORMS).filter(([k]) => ["linkedin", "facebook", "instagram", "pinterest"].includes(k)).map(([k, v]) =>
          <button key={k} className={platform === k ? "on" : ""} style={{ "--plat": v.color }} onClick={() => setPlatform(k)}>{v.label}</button>)}</div>
        <PostPreview platform={platform} text={text} poster={imageNode(image, headline, sub, tag)} />
      </section>
    </div>
    <footer className="composer-foot">
      <button className="primary-button" disabled={busy || !text.trim()} onClick={save} data-testid="social-new-save">{busy ? <Loader2 size={14} className="spin" /> : <Check size={14} />} Save post</button>
    </footer>
  </div></div>, document.body);
}

function Editor({ post, accounts, limits, onChanged, onDeleted }) {
  const [title, setTitle] = useState(post.title);
  const [text, setText] = useState(post.text);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [addLogo, setAddLogo] = useState(true);
  const fileRef = useRef(null);
  const videoRef = useRef(null);

  const postable = accounts.filter(a => a.can_post);
  useEffect(() => {
    setTitle(post.title); setText(post.text);
    setPicked(postable.filter(a => post.results?.[a.id]?.status !== "posted").map(a => a.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.id, accounts.length]);

  const dirty = title !== post.title || text !== post.text;
  const remaining = postable.filter(a => post.results?.[a.id]?.status !== "posted");
  const ready = picked.filter(id => post.results?.[id]?.status !== "posted" && post.blockers?.[id] === null);
  const hasPlaceholders = /\[\[.+?\]\]/.test(text);
  const name = (id) => accounts.find(a => a.id === id)?.display_name || "account";
  const groups = [["Mine", postable.filter(a => a.owner !== "company")], ["Company", postable.filter(a => a.owner === "company")]];

  const save = async () => {
    setBusy("save");
    try { onChanged(await updateSocialPost(post.id, { title, text })); toast.success("Saved"); return true; }
    catch (e) { toast.error(errText(e, "Could not save")); return false; }
    finally { setBusy(""); }
  };
  const upload = async (file) => {
    if (!file) return;
    setBusy("image");
    try { onChanged(await attachSocialImage(post.id, file.name, await readAsBase64(file), title, addLogo)); toast.success("Image attached"); }
    catch (e) { toast.error(errText(e, "Could not attach the image")); }
    finally { setBusy(""); if (fileRef.current) fileRef.current.value = ""; }
  };
  const uploadVideo = async (file) => {
    if (!file) return;
    if (file.size > MAX_VIDEO_MB * 1024 * 1024) { toast.error(`Videos must be under ${MAX_VIDEO_MB} MB`); return; }
    setBusy("video");
    try { onChanged(await attachSocialVideo(post.id, file.name, file.type, await readAsBase64(file))); toast.success("Video attached"); }
    catch (e) { toast.error(errText(e, "Could not attach the video")); }
    finally { setBusy(""); if (videoRef.current) videoRef.current.value = ""; }
  };
  const generate = async () => {
    if (dirty && !(await save())) return;
    setBusy("image");
    try { onChanged(await generateSocialImage(post.id)); toast.success("Image generated"); }
    catch (e) { toast.error(errText(e, "Could not generate the image")); }
    finally { setBusy(""); }
  };
  const dropImage = async () => {
    setBusy("image");
    try { onChanged(await removeSocialImage(post.id)); } catch { toast.error("Could not remove the image"); }
    finally { setBusy(""); }
  };
  const dropVideo = async () => {
    setBusy("video");
    try { onChanged(await removeSocialVideo(post.id)); } catch { toast.error("Could not remove the video"); }
    finally { setBusy(""); }
  };
  const publish = async () => {
    if (dirty && !(await save())) return;
    setBusy("post");
    try {
      const updated = await publishSocialPost(post.id, ready);
      onChanged(updated);
      const r = updated.results || {};
      const posted = ready.filter(id => r[id]?.status === "posted");
      if (posted.length) toast.success(`Posted to ${posted.map(name).join(", ")}`);
      ready.filter(id => r[id]?.status !== "posted").forEach(id => toast.error(`${name(id)}: ${r[id]?.error || "not posted"}`));
      posted.filter(id => r[id]?.note).forEach(id => toast.info(`${name(id)}: ${r[id].note}`));
    } catch (e) { toast.error(errText(e, "Could not post")); }
    finally { setBusy(""); setConfirming(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${post.title}" from the CRM? (Anything already posted stays on the platforms.)`)) return;
    try { await deleteSocialPost(post.id); onDeleted(post.id); } catch { toast.error("Could not delete"); }
  };

  return <div className="panel social-editor" data-testid="social-editor">
    <div className="social-editor-head">
      <div><Tag tone="blue">{post.idea_no ? `IDEA ${post.idea_no}` : "CUSTOM"}</Tag> <Tag>{post.category}</Tag>{post.ai_generated && <> <Tag tone="purple">AI-assisted</Tag></>}</div>
      <button className="icon-button small" title="Delete post" onClick={remove} data-testid="social-delete"><Trash2 size={14} /></button>
    </div>

    <label><span>Title <small>(only you see this; also the video title on YouTube)</small></span><input value={title} onChange={e => setTitle(e.target.value)} data-testid="social-title" /></label>
    <label>Post text<textarea value={text} onChange={e => setText(e.target.value)} rows={12} data-testid="social-text" /></label>
    <div className="social-counts">{limits.map(p => <span key={p.key} className={text.length > p.text_limit ? "over" : ""}>{p.label} {text.length.toLocaleString()}/{p.text_limit.toLocaleString()}</span>)}</div>
    {hasPlaceholders && <div className="alert-strip"><AlertCircle size={14} /><span>Replace every <code>[[…]]</code> with the real details before posting.</span></div>}

    <div className="social-image">
      {post.image
        ? <><a href={mediaUrl(post.image.original)} target="_blank" rel="noreferrer" title="Open full size"><img src={mediaUrl(post.image.original)} alt={post.image.alt_text || post.title} /></a>
            <div><b>{post.image_source === "generated" ? "Generated image" : post.image.filename}</b><small>{post.image.width}×{post.image.height}{post.image.ig_padded ? " · padded to 4:5 for Instagram" : ""}</small>
              <div className="social-image-actions">
                <button className="outline-button" onClick={generate} disabled={!!busy} data-testid="social-regenerate-image">{busy === "image" ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />} Regenerate</button>
                <button className="outline-button" onClick={() => fileRef.current?.click()} disabled={!!busy}><ImagePlus size={14} /> Upload my own</button>
                <button className="outline-button" onClick={dropImage} disabled={!!busy}><X size={14} /> Remove</button>
              </div></div></>
        : <div className="social-image-empty"><ImagePlus size={20} /><div><b>No image yet</b><small>Instagram and Pinterest need one. Generate a branded image or upload your own.</small></div>
            <button className="primary-button" onClick={generate} disabled={!!busy} data-testid="social-generate-image">{busy === "image" ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />} Generate</button>
            <button className="outline-button" onClick={() => fileRef.current?.click()} disabled={!!busy} data-testid="social-add-image"><ImagePlus size={14} /> Upload</button></div>}
      <label className="social-logo-toggle" title="Uploads get the logo in the top-left corner"><input type="checkbox" checked={addLogo} onChange={e => setAddLogo(e.target.checked)} data-testid="social-upload-logo" /> Add logo to uploads</label>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={e => upload(e.target.files?.[0])} data-testid="social-image-input" />
    </div>

    <div className="social-image">
      {post.video
        ? <div className="social-image-empty"><Film size={20} /><div><b>{post.video.filename}</b><small>{(post.video.size / 1024 / 1024).toFixed(1)} MB · goes to TikTok and YouTube</small></div>
            <button className="outline-button" onClick={() => videoRef.current?.click()} disabled={!!busy}><Film size={14} /> Replace</button>
            <button className="outline-button" onClick={dropVideo} disabled={!!busy}><X size={14} /> Remove</button></div>
        : <div className="social-image-empty"><Film size={20} /><div><b>No video</b><small>YouTube only takes videos; TikTok takes a video or the image. MP4, MOV or WebM, up to {MAX_VIDEO_MB} MB.</small></div>
            <button className="outline-button" onClick={() => videoRef.current?.click()} disabled={!!busy} data-testid="social-add-video">{busy === "video" ? <Loader2 size={14} className="spin" /> : <Film size={14} />} Add video</button></div>}
      <input ref={videoRef} type="file" accept="video/mp4,video/quicktime,video/webm" hidden onChange={e => uploadVideo(e.target.files?.[0])} data-testid="social-video-input" />
    </div>

    {postable.length === 0 && <div className="alert-strip"><Users size={14} /><span>No accounts to post to yet — connect one under <b>Accounts</b> above.</span></div>}
    {groups.filter(([, list]) => list.length).map(([label, list]) => <div key={label}>
      <small className="eyebrow">{label.toUpperCase()}</small>
      <div className="social-platforms">
        {list.map(a => {
          const res = post.results?.[a.id];
          const block = post.blockers?.[a.id];
          const done = res?.status === "posted";
          return <div className="social-platform" key={a.id} data-testid={`social-account-${a.id}`}>
            <label className="social-check" style={{ width: 220 }}>
              <input type="checkbox" disabled={done || !!block} checked={done || (picked.includes(a.id) && !block)}
                onChange={e => setPicked(ps => e.target.checked ? [...ps, a.id] : ps.filter(k => k !== a.id))} />
              <b>{a.display_name}</b><small className="muted">{limits.find(l => l.key === a.platform)?.label}</small>
            </label>
            <div className="social-platform-state">
              {done ? <><Tag tone="green">Posted {when(res.at)}</Tag>{res.url && <a href={res.url} target="_blank" rel="noreferrer">View <ExternalLink size={11} /></a>}{res.note && <small>{res.note}</small>}</>
                : res ? <><Tag tone={RESULT_TONE[res.status]}>{res.status === "failed" ? "Failed" : "Skipped"} {when(res.at)}</Tag><small>{res.error}</small></>
                : block ? <small>{block}</small> : <small className="ok">Ready</small>}
            </div>
          </div>;
        })}
      </div>
    </div>)}

    <div className="social-actions">
      <button className="outline-button" onClick={save} disabled={!dirty || !!busy} data-testid="social-save">{busy === "save" ? <Loader2 size={14} className="spin" /> : <Check size={14} />} Save</button>
      <button className="primary-button" disabled={!!busy || remaining.length === 0 || ready.length === 0 || hasPlaceholders}
        onClick={() => setConfirming(true)} data-testid="social-post"
        title={hasPlaceholders ? "Fill in the [[…]] placeholders first" : ready.length === 0 ? "No selected account is ready" : undefined}>
        {busy === "post" ? <Loader2 size={14} className="spin" /> : <Send size={14} />} {postable.length && remaining.length === 0 ? "Posted everywhere" : `Post${ready.length ? ` to ${ready.length}` : ""}`}
      </button>
    </div>
    {confirming && <ConfirmPublish post={{ ...post, title }} names={ready.map(name)} busy={busy === "post"} close={() => setConfirming(false)} onConfirm={publish} />}
  </div>;
}

export default function SocialView() {
  const [posts, setPosts] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [providerList, setProviderList] = useState([]);
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("posts");
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [newOpen, setNewOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [generating, setGenerating] = useState(false);

  const loadAccounts = async () => {
    const a = await listSocialAccounts();
    setAccounts(a.accounts); setProviderList(a.providers);
    return a;
  };
  const load = async () => {
    try {
      const [p, , u] = await Promise.all([listSocialPosts(), loadAccounts(), getMe()]);
      setPosts(p); setMe(u);
      setSelectedId(id => id || p[0]?.id || null);
    } catch { toast.error("Could not load social posts"); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const replace = (p) => setPosts(ps => ps.some(x => x.id === p.id) ? ps.map(x => x.id === p.id ? p : x) : [...ps, p]);
  const generateAll = async () => {
    setGenerating(true);
    try {
      const { generated } = await generateMissingSocialImages();
      toast.success(`${generated} image${generated === 1 ? "" : "s"} generated`);
      await load();
    } catch (e) { toast.error(errText(e, "Could not generate images")); }
    finally { setGenerating(false); }
  };

  const filtered = useMemo(() => (posts || []).filter(p => {
    if (filter === "ready") return p.status !== "posted" && p.placeholders.length === 0;
    if (filter === "input") return p.placeholders.length > 0;
    if (filter === "posted") return p.status === "posted" || p.status === "partial";
    return true;
  }), [posts, filter]);

  if (!posts || !me) return <div className="page-view"><div className="panel" style={{ padding: 24 }}>Loading…</div></div>;
  const selected = posts.find(p => p.id === selectedId);
  const count = (f) => posts.filter(p => f === "ready" ? p.status !== "posted" && p.placeholders.length === 0
    : f === "input" ? p.placeholders.length > 0 : p.status === "posted" || p.status === "partial").length;
  const postable = accounts.filter(a => a.can_post);
  const usedPlatforms = providerList.filter(p => postable.some(a => a.platform === p.key));

  return <div className="page-view" data-testid="social-view">
    <div className="view-toolbar">
      <div><div className="eyebrow"><span className="eyebrow-dot" /> SOCIAL MEDIA</div><h2>Social posts</h2>
        <p>Write once, then post to any connected account: yours, or the company's. {postable.length} account{postable.length === 1 ? "" : "s"} ready for you.</p></div>
      <div className="toolbar-actions">
        {tab === "posts" && posts.some(p => !p.image) && <button className="outline-button" onClick={generateAll} disabled={generating} data-testid="social-generate-all">{generating ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />} {generating ? "Generating…" : `Generate missing images (${posts.filter(p => !p.image).length})`}</button>}
        <button className="outline-button" onClick={load} data-testid="social-refresh"><RefreshCw size={14} /> Refresh</button>
        {tab === "posts" && <button className="outline-button" onClick={() => setAiOpen(true)} data-testid="social-ai"><Sparkles size={14} /> Write with AI</button>}
        {tab === "posts" && <button className="primary-button" onClick={() => setNewOpen(true)} data-testid="social-new"><Plus size={14} /> New post</button>}
      </div>
    </div>

    <div className="filter-row">
      <button className={tab === "posts" ? "active-tab" : ""} onClick={() => setTab("posts")} data-testid="social-tab-posts">Posts</button>
      <button className={tab === "accounts" ? "active-tab" : ""} onClick={() => setTab("accounts")} data-testid="social-tab-accounts">Accounts <span>{accounts.length}</span></button>
    </div>

    {tab === "accounts" ? <AccountsPanel me={me} onChanged={() => { loadAccounts(); listSocialPosts().then(setPosts).catch(() => {}); }} /> : <>
      {postable.length === 0 && <div className="alert-strip" data-testid="social-setup-note"><Users size={14} />
        <span>Nothing connected yet. Open <b>Accounts</b> and connect your LinkedIn, Facebook Page, Instagram, TikTok, YouTube or Pinterest.</span></div>}
      <div className="filter-row">
        {[["all", `All ${posts.length}`], ["ready", "Ready", count("ready")], ["input", "Needs your input", count("input")], ["posted", "Posted", count("posted")]].map(([k, label, n]) =>
          <button key={k} className={filter === k ? "active-tab" : ""} onClick={() => setFilter(k)} data-testid={`social-filter-${k}`}>{label}{n !== undefined && <span>{n}</span>}</button>)}
      </div>
      <div className="social-layout">
        <div className="panel social-list">
          {filtered.length === 0 && <div className="empty-row">Nothing here.</div>}
          {filtered.map(p => {
            const st = POST_STATUS[p.status] || POST_STATUS.draft;
            return <button key={p.id} className={`social-row ${p.id === selectedId ? "active" : ""}`} onClick={() => setSelectedId(p.id)} data-testid={`social-row-${p.idea_no || p.id}`}>
              <span className="social-no">{p.idea_no || "•"}</span>
              <div><b>{p.title}</b><small>{p.category}{p.image ? " · image" : ""}{p.video ? " · video" : ""}{p.placeholders.length ? " · needs input" : ""}</small></div>
              <Tag tone={st.tone}>{st.label}</Tag>
            </button>;
          })}
        </div>
        {selected
          ? <Editor key={selected.id} post={selected} accounts={accounts} limits={usedPlatforms.length ? usedPlatforms : providerList.slice(0, 3)} onChanged={replace}
              onDeleted={(id) => { setPosts(ps => ps.filter(x => x.id !== id)); setSelectedId(null); }} />
          : <div className="panel social-editor"><p>Pick a post on the left.</p></div>}
      </div>
    </>}

    {aiOpen && <SocialAiModal close={() => setAiOpen(false)} onSaved={(p) => replace(p)} />}
    {newOpen && <NewPostModal close={() => setNewOpen(false)} onCreated={(p) => { replace(p); setSelectedId(p.id); }} onWriteWithAi={() => { setNewOpen(false); setAiOpen(true); }} />}
  </div>;
}
